import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import { AuthService } from 'src/app/auth.service';
import { CustomPaginatorComponent } from 'src/app/shared/components/custom-paginator/custom-paginator.component';
import { ContentDisplayCardComponent } from 'src/app/shared/components/ui/content-display-card/content-display-card.component';
import { ContentShellComponent } from 'src/app/shared/components/video-call-drawer-chime/in-content-video-call-shell/content-shell.component';
import { MaterialModule } from 'src/app/shared/material.module';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { ContentService } from 'src/app/views/content/content.service';
import { ContentStateService } from '../content/content-state.service';
import { QuestionViewerComponent } from './question-viewer/question-viewer.component';

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.scss'],
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MarkdownModule,
    ContentShellComponent,
    ContentDisplayCardComponent,
    CustomPaginatorComponent,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionsComponent implements OnInit {
  private dialog = inject(MatDialog);
  private confirmMenuService = inject(ConfirmMenuService);
  private fb = inject(UntypedFormBuilder);
  private authService = inject(AuthService);
  private contentService = inject(ContentService);
  private stateService = inject(ContentStateService);

  questions = signal<any[]>([]);
  showUnansweredOnly = signal(false);
  totalLength = signal(0);
  pageIndex = signal(0);
  pageSizeOptions = [10, 20, 30, 40, 50];
  pageSize = signal(this.pageSizeOptions[0]);
  loading = signal(false);

  // Search Form
  searchForm: UntypedFormGroup;

  // Signal to track search term input
  searchTermSignal = signal('');

  // Computed filtered questions
  filteredQuestions = computed(() => {
    let questions = this.questions();
    const searchTerm = this.searchTermSignal().toLowerCase();
    const unanswered = this.showUnansweredOnly();

    if (unanswered) {
      questions = questions.filter((q) => q.notAnswered);
    }

    if (searchTerm) {
      questions = questions.filter((q) => q.query.toLowerCase().includes(searchTerm));
    }

    return questions;
  });

  searchTypes: any[] = [
    { label: 'Search.AllQuestions', value: 'AllQuestions' },
    { label: 'Search.QuestionsWithoutAnswer', value: 'QuestionsWithoutAnswer' },
  ];

  constructor() {
    this.searchForm = this.fb.group({
      searchTerm: ['', Validators.required],
      searchType: ['AllQuestions', Validators.required],
    });
  }

  ngOnInit(): void {
    this.getQuestions();
  }

  getQuestions(event?) {
    if (event) {
      this.showUnansweredOnly.set(event.value === 'QuestionsWithoutAnswer');
    }
    this.loading.set(true);
    this.contentService
      .getAIQuestions({
        pageIndex: this.pageIndex(),
        pageSize: this.pageSize(),
        query: this.searchTermSignal(),
        showUnansweredOnly: this.showUnansweredOnly(),
      })
      .subscribe({
        next: (questions) => {
          this.loading.set(false);
          this.questions.set(questions.results);
          this.totalLength.set(questions.totalCount[0].total);
        },
        error: (err) => {
          console.error(err);
          this.loading.set(false);
        },
      });
  }

  openQuestion(question) {
    this.dialog.open(QuestionViewerComponent, {
      data: question,
      width: '900px',
      maxWidth: '90vw',
    });
  }

  deleteAIResponse(id: string, event?: Event) {
    if (event) {
      event.stopPropagation();
      this.confirmMenuService.confirm(event.target as HTMLElement, 'Global.AreYouSure').subscribe(() => {
        this.contentService.deleteAIAnswer(id).subscribe(() => {
          this.getQuestions();
        });
      });
    } else {
      this.contentService.deleteAIAnswer(id).subscribe(() => {
        this.getQuestions();
      });
    }
  }

  onPageChange($event) {
    this.pageIndex.set($event.page);
    this.pageSize.set($event.pageSize);
    this.getQuestions();
  }

  getAnswer(question) {
    return question.AIResponse || '';
  }

  userIsNotReader() {
    const userInfo = this.authService.userInfo();
    return userInfo.type === 'Writer' || userInfo.type === 'Admin';
  }

  openAnalysisSidebar() {
    this.stateService.openQuestionsAnalysisSidebar(true);
  }

  openSearch() {
    this.stateService.openQuestionsSearchSidebar(true);
  }
}
